/* eslint-disable lyrahealth/no-react-native-text */
import React from 'react'

import { Image, StyleSheet, Text, View } from '@react-pdf/renderer'
import { addDays, format, parseISO } from 'date-fns'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { careConsents, LegacyUIMetadata } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/constants'
import {
  AssignmentStatuses,
  AssignmentStatusesLabel,
} from '@lyrahealth-inc/shared-app-logic/src/features/assignments/types'
import { getIsClientIntake } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { lightThemeColors } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/colors'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Assignment } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { FieldSchema, UiMetadata } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

import FormItemPDF, { renderStyledStrings } from './FormItemPDF'
import ItemListPDF from './ItemListPDF'
import { type RecordsPdfAssignmentsSession } from './recordsPdfSections'

type ClientAssignmentsPDFProps = {
  appointments?: Appointment[]
  assignments: Assignment[]
  pdfContentStartDate?: string
  pdfContentEndDate?: string
  providerEpisodesInfo: { text: string; id: string }[]
}

const styles = StyleSheet.create({
  container: {
    marginTop: 20,
  },
  sectionTitle: {
    fontWeight: 'bold',
    fontSize: 18,
    color: lightThemeColors.textActive,
    borderBottomWidth: 1,
    borderBottomColor: lightThemeColors.textActive,
    marginBottom: 20,
    paddingBottom: 5,
  },
  episodeTitle: {
    fontSize: 18,
    color: lightThemeColors.textSecondary,
    borderBottomWidth: 1,
    borderBottomColor: lightThemeColors.textSecondary,
    marginBottom: 20,
    paddingBottom: 5,
  },
  sessionContainer: {
    marginBottom: 20,
  },
  subheader: {
    fontSize: 16,
    color: lightThemeColors.textPrimary,
    marginBottom: 12,
  },
  assignmentItem: {
    marginBottom: 8,
  },
  assignmentListItemContainer: {
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: lightThemeColors.borderDefault,
  },
  assignmentInstructions: {
    fontSize: 10,
    color: lightThemeColors.textSecondary,
    lineHeight: 1.4,
    marginBottom: 2,
  },
  instructionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: lightThemeColors.textPrimary,
    marginBottom: 8,
  },
  lessonItemsContainer: {
    width: 464,
    alignSelf: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  assignmentItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: lightThemeColors.borderDefault,
    alignItems: 'center',
    width: 400,
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: 24,
  },
  assignmentItemTextContainer: {
    flexShrink: 1,
    flexGrow: 1,
    flexBasis: 0,
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
  assignmentEntryContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: lightThemeColors.borderDefault,
    alignItems: 'center',
    width: 400,
    minHeight: '100px',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: 24,
  },
  noEntriesTextContainer: {
    fontSize: 12,
    color: lightThemeColors.textSecondary,
    textAlign: 'center',
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: lightThemeColors.borderDefault,
    width: 400,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  videoThubmnailContainer: {
    alignItems: 'flex-start',
  },
  lessonItemDescription: {
    fontWeight: 'bold',
    fontSize: 10,
    color: lightThemeColors.textSecondary,
    textTransform: 'uppercase',
  },
  lessonItemTitle: {
    marginTop: 8,
    fontSize: 14,
    color: lightThemeColors.textPrimary,
  },
  lessonItemDetails: {
    marginTop: 8,
    fontSize: 10,
    color: lightThemeColors.textSecondary,
  },
  assignmentContainer: {
    marginBottom: 20,
    padding: 16,
    border: `1px solid ${lightThemeColors.borderDefault}`,
    borderRadius: 8,
    backgroundColor: lightThemeColors.backgroundPrimary,
    minHeight: '100px',
  },
  assignmentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: `1px solid ${lightThemeColors.borderDefault}`,
  },
  assignmentTitle: {
    fontWeight: 'bold',
    fontSize: 14,
    color: lightThemeColors.textPrimary,
  },
  assignmentStatus: {
    fontSize: 10,
    color: lightThemeColors.textSecondary,
    textTransform: 'uppercase',
    backgroundColor: lightThemeColors.cardBackgroundSecondary,
    padding: '4px 8px',
    borderRadius: 4,
  },
  assignmentMetadata: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  metadataItem: {
    fontSize: 10,
    color: lightThemeColors.textSecondary,
  },
  emptyMessage: {
    fontSize: 12,
    color: lightThemeColors.textSecondary,
    textAlign: 'center',
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: lightThemeColors.borderDefault,
  },
})

const getAssignmentType = (assignment: Assignment): string => {
  const group = assignment.content.group
  switch (group) {
    case 'lesson':
      return 'Lesson'
    case 'exercise':
      return 'Exercise'
    case 'assessment':
      return 'Assessment'
    case 'infosheet':
      return 'Guide'
    default:
      return 'Unknown'
  }
}

const sortAssignmentsWithinSession = (assignments: Assignment[]): Assignment[] => {
  const isCareFeedback = (assignment: Assignment): boolean => {
    return assignment.content.title === 'Care Feedback'
  }

  return assignments.sort((a, b) => {
    const getPriority = (assignment: Assignment): number => {
      if (assignment.content.group === 'assessment' && !isCareFeedback(assignment)) return 1
      if (isCareFeedback(assignment)) return 2
      if (assignment.content.group === 'lesson') return 3
      if (assignment.content.group === 'exercise') return 4
      if (assignment.content.group === 'infosheet') return 5
      return 6
    }
    const aPriority = getPriority(a)
    const bPriority = getPriority(b)
    if (aPriority !== bPriority) {
      return aPriority - bPriority
    }
    return new Date(a.create_date).getTime() - new Date(b.create_date).getTime()
  })
}

const sortCareOnboardingAssignments = (assignments: Assignment[]): Assignment[] => {
  return assignments.sort((a, b) => {
    const getPriority = (assignment: Assignment): number => {
      if (careConsents.includes(assignment.content.name)) return 1
      if (getIsClientIntake(assignment.content.name)) return 2
      if (
        assignment.content.name.toLowerCase().includes('initialassessment') ||
        assignment.content.title.toLowerCase().includes('initial assessment')
      )
        return 3
      return 4
    }

    const aPriority = getPriority(a)
    const bPriority = getPriority(b)

    if (aPriority !== bPriority) {
      return aPriority - bPriority
    }

    return new Date(a.create_date).getTime() - new Date(b.create_date).getTime()
  })
}

const LessonAssignmentItem: React.FC<{ assignment: Assignment }> = ({ assignment }) => {
  const completedResponse = assignment.assignment_responses?.find((response) => response.status === 'completed')
  if (!assignment.content_meta_data?.contents) {
    return null
  }
  let assignmentStatus = assignment.assignment_responses?.some(
    (response) =>
      Object.values(response).some((status) => ['completed', 'in_progress'].includes(status)) ||
      ['completed', 'in_progress'].includes(response.status),
  )
    ? 'In Progress'
    : 'Not Started'

  if (
    assignment.assignment_responses?.some(
      (response) =>
        Object.values(response).every((status) => status === 'completed') || response.status === 'completed',
    )
  ) {
    assignmentStatus = 'Completed'
  }

  return (
    <View style={styles.assignmentListItemContainer}>
      <AssignmentItemHeader
        assignmentIstructions={assignment.instructions}
        assignmentTitle={assignment.content.title}
        assignmentStatus={assignmentStatus}
        assignmentSubmissionDate={completedResponse?.submit_date}
      />
      <View style={styles.lessonItemsContainer}>
        {assignment.content_meta_data.contents.map((content, index) => {
          const isVideo = content.meta_data.thumbnail !== undefined
          const contentStatus = assignment.assignment_responses?.[0]?.response[content.name]?.status
            ? AssignmentStatusesLabel[
                assignment.assignment_responses?.[0]?.response[content.name]?.status as AssignmentStatuses
              ]
            : assignmentStatus

          if (isVideo) {
            const videoThumbnail =
              isVideo && content.meta_data.thumbnail ? (
                <Image src={content.meta_data.thumbnail} style={{ width: 170, height: 98, borderRadius: 6 }} />
              ) : undefined
            const videoDurationSeconds =
              assignment.assignment_responses?.[0]?.response[content.name]?.totalDuration ?? 0
            const videoDurationMinutes = videoDurationSeconds ? Math.floor(videoDurationSeconds / 60) : undefined
            return (
              <View key={index} style={styles.assignmentItemContainer} wrap={false}>
                {videoThumbnail && <View style={styles.videoThubmnailContainer}>{videoThumbnail}</View>}
                <View style={styles.assignmentItemTextContainer}>
                  <Text style={styles.lessonItemDescription}>
                    Part {index + 1} • Video
                    {videoDurationMinutes && ` • ${videoDurationMinutes} min`}
                  </Text>
                  <Text style={styles.lessonItemTitle}>{content.title}</Text>
                  <Text key={index} style={styles.lessonItemDetails}>
                    {contentStatus}
                  </Text>
                </View>
              </View>
            )
          }
          const legacyUiSchema = (LegacyUIMetadata as Record<string, { uiSchema?: UiMetadata }>)[content.name]?.uiSchema
          const uiSchema = content.meta_data?.uiSchema ?? legacyUiSchema
          const uiSchemaOrder =
            content.meta_data?.uiSchema?.['ui:order'] ??
            legacyUiSchema?.['ui:order'] ??
            (completedResponse && Object.keys(completedResponse.response[content.name].data))
          return (
            <View key={index}>
              <View style={styles.assignmentItemContainer}>
                <View style={styles.assignmentItemTextContainer}>
                  <View>
                    <Text style={styles.lessonItemDescription}>
                      Review
                      {content.estimatedCompletionTimeMinutes && ` • ${content.estimatedCompletionTimeMinutes} min`}
                    </Text>
                    <Text style={styles.lessonItemTitle}>{content.title}</Text>
                    <Text key={index} style={styles.lessonItemDetails}>
                      {contentStatus}
                    </Text>
                  </View>
                </View>
              </View>
              {completedResponse && (
                <View key={index} style={styles.assignmentEntryContainer}>
                  <View style={styles.assignmentItemTextContainer}>
                    <AssignmentItemHeader
                      assignmentTitle={content.title}
                      assignmentStatus={contentStatus}
                      assignmentSubmissionDate={completedResponse.submit_date}
                    />
                    <FormItemPDF
                      formBody={completedResponse.response[content.name].data}
                      formUISchema={uiSchema}
                      formSchema={content.meta_data?.schema as FieldSchema}
                      formUISchemaOrder={uiSchemaOrder}
                    />
                  </View>
                </View>
              )}
            </View>
          )
        })}
      </View>
    </View>
  )
}

const ExerciseAssignmentItem: React.FC<{ assignment: Assignment }> = ({ assignment }) => {
  const assignmentStatus = assignment.assignment_responses?.some((response) =>
    Object.values(response).some((status) => status === 'completed'),
  )
    ? 'Completed'
    : 'Not Started'

  const submittedEntries = assignment.assignment_responses?.filter((response) => response.status === 'completed')

  return (
    <View style={styles.assignmentListItemContainer}>
      <AssignmentItemHeader
        assignmentIstructions={assignment.instructions}
        assignmentTitle={assignment.content.title}
        assignmentStatus={assignmentStatus}
        assignmentSubmissionDate={submittedEntries?.[0]?.submit_date}
      />
      {!submittedEntries || submittedEntries.length === 0 ? (
        <Text style={styles.noEntriesTextContainer}>No submitted entries</Text>
      ) : (
        <View>
          {submittedEntries.map((assignmentResponse, index) => (
            <View key={index} style={styles.assignmentEntryContainer}>
              <View style={styles.assignmentItemTextContainer}>
                <AssignmentItemHeader
                  assignmentTitle={assignment.content.title}
                  assignmentStatus={assignmentResponse.status}
                  assignmentSubmissionDate={assignmentResponse.submit_date}
                />
                <FormItemPDF
                  formBody={assignmentResponse.response}
                  formUISchema={assignment.content_meta_data?.uiSchema}
                  formSchema={assignment.content_meta_data?.schema as FieldSchema}
                  formUISchemaOrder={
                    assignment.content_meta_data?.uiSchema?.['ui:order'] ?? Object.keys(assignmentResponse.response)
                  }
                />
              </View>
            </View>
          ))}
        </View>
      )}
    </View>
  )
}

const InfosheetAssignmentItem: React.FC<{ assignment: Assignment }> = ({ assignment }) => {
  if (!assignment.content_meta_data?.content) {
    return null
  }
  return (
    <View style={styles.assignmentListItemContainer}>
      <AssignmentItemHeader
        assignmentIstructions={assignment.instructions}
        assignmentTitle={assignment.content.title}
      />
      <View style={styles.assignmentInstructions}>{renderStyledStrings(assignment.content_meta_data?.content)}</View>
    </View>
  )
}

const AssignmentItemHeader: React.FC<{
  assignmentIstructions?: string | null
  assignmentTitle?: string
  assignmentStatus?: string
  assignmentSubmissionDate?: string | null
}> = ({ assignmentIstructions, assignmentTitle, assignmentStatus, assignmentSubmissionDate }) => {
  return (
    <View>
      <View style={styles.assignmentHeader}>
        {assignmentTitle && <Text style={styles.assignmentTitle}>{assignmentTitle}</Text>}
        {assignmentStatus && <Text style={styles.assignmentStatus}>{assignmentStatus}</Text>}
      </View>
      {assignmentSubmissionDate && (
        <View style={styles.assignmentMetadata}>
          <Text style={styles.metadataItem}>Submitted: {format(parseISO(assignmentSubmissionDate), 'MM/dd/yyyy')}</Text>
        </View>
      )}
      {assignmentIstructions && (
        <View style={{ marginTop: 4 }}>
          <Text style={styles.instructionTitle}>Introduction</Text>
          <View style={styles.assignmentInstructions}>{renderStyledStrings(assignmentIstructions)}</View>
        </View>
      )}
    </View>
  )
}

const AssignmentItem: React.FC<{ assignment: Assignment }> = ({ assignment }) => {
  if (
    !assignment.assignment_responses ||
    assignment.assignment_responses.length === 0 ||
    !assignment.assignment_responses.some((response) => response.status === 'completed')
  ) {
    return null
  }

  return (
    <View style={styles.assignmentItem}>
      {assignment.assignment_responses.map((assignmentResponse, index) => {
        if (assignmentResponse.status !== 'completed') {
          return null
        }
        return (
          <View style={styles.assignmentContainer} key={index}>
            <AssignmentItemHeader
              assignmentIstructions={assignment.instructions}
              assignmentTitle={assignment.content.title}
              assignmentStatus={assignmentResponse.status}
              assignmentSubmissionDate={assignmentResponse.submit_date}
            />

            <View style={{ marginTop: 4 }}>
              <FormItemPDF
                key={assignment.id}
                formBody={assignmentResponse.response}
                formUISchema={assignment.content_meta_data?.uiSchema}
                formSchema={assignment.content_meta_data?.schema as FieldSchema}
                formUISchemaOrder={
                  assignment.content_meta_data?.uiSchema?.['ui:order'] ?? Object.keys(assignmentResponse.response)
                }
              />
            </View>
          </View>
        )
      })}
    </View>
  )
}

const AssignmentSection: React.FC<{ assignments: Assignment[] }> = ({ assignments }) => {
  if (!assignments || assignments.length === 0) {
    return null
  }

  return (
    <View style={{ marginTop: 16 }}>
      {assignments
        .sort((a, b) => new Date(a.create_date).getTime() - new Date(b.create_date).getTime())
        .map((assignment, index) => {
          if (assignment.content.group === 'lesson') {
            return <LessonAssignmentItem key={assignment.id || index} assignment={assignment} />
          }
          if (assignment.content.group === 'exercise') {
            return <ExerciseAssignmentItem key={assignment.id || index} assignment={assignment} />
          }
          if (assignment.content.group === 'infosheet') {
            return <InfosheetAssignmentItem key={assignment.id || index} assignment={assignment} />
          }
          return <AssignmentItem key={assignment.id || index} assignment={assignment} />
        })}
    </View>
  )
}

const AssignmentsBySession = ({ assignments }: { assignments: Assignment[] }): RecordsPdfAssignmentsSession[] => {
  const isCareConsent = (assignment: Assignment): boolean => {
    return careConsents.includes(assignment.content.name)
  }
  const isInitialAssessment = (assignment: Assignment): boolean => {
    return (
      assignment.content.name.toLowerCase().includes('initialassessment') ||
      assignment.content.title.toLowerCase().includes('initial assessment')
    )
  }
  // Standalone care consents are split out of care onboarding so they land in their own PDF
  // document and can be excluded from automatic redaction (LCDELIVERY-7915). Redaction exclusion
  // works on page ranges, so they must not share pages with the client intake, which does contain
  // client-specific detail and stays fully redactable. See recordsPdfSections.ts.
  const careConsentAssignments = assignments.filter(isCareConsent)
  const careOnboardingAssignments = assignments.filter(
    (assignment) =>
      !isCareConsent(assignment) && (getIsClientIntake(assignment.content.name) || isInitialAssessment(assignment)),
  )
  const sessionAssignments = assignments.filter(
    (assignment) =>
      !isCareConsent(assignment) && !getIsClientIntake(assignment.content.name) && !isInitialAssessment(assignment),
  )
  const assignmentsBySession = sessionAssignments.reduce(
    (acc, assignment) => {
      const sessionPeriod = assignment.session_period || 0
      if (!acc[sessionPeriod]) {
        acc[sessionPeriod] = []
      }
      acc[sessionPeriod].push(assignment)
      return acc
    },
    {} as Record<number, Assignment[]>,
  )
  const sortedSessions = Object.keys(assignmentsBySession)
    .map(Number)
    .sort((a, b) => a - b)

  const renderedAssignmentsBySession: RecordsPdfAssignmentsSession[] = []

  if (careConsentAssignments.length > 0) {
    const sortedCareConsentAssignments = sortCareOnboardingAssignments(careConsentAssignments)
    renderedAssignmentsBySession.push({
      sectionKey: 'careConsents',
      sessionName: 'Lyra Consent for Care',
      content: (
        <View style={styles.sessionContainer}>
          <ItemListPDF
            items={sortedCareConsentAssignments.map((assignment) => {
              const assignmentType = getAssignmentType(assignment)
              const status = AssignmentStatusesLabel[assignment.status as AssignmentStatuses] || assignment.status

              return {
                title: assignment.content.title,
                details: [assignmentType, status],
              }
            })}
          />
          <AssignmentSection assignments={sortedCareConsentAssignments} />
        </View>
      ),
    })
  }

  if (careOnboardingAssignments.length > 0) {
    renderedAssignmentsBySession.push({
      sectionKey: 'assignments',
      sessionName: 'Care Onboarding Forms',
      content: (
        <View style={styles.sessionContainer}>
          <ItemListPDF
            items={sortCareOnboardingAssignments(careOnboardingAssignments).map((assignment) => {
              const assignmentType = getAssignmentType(assignment)
              const status = AssignmentStatusesLabel[assignment.status as AssignmentStatuses] || assignment.status

              return {
                title: assignment.content.title,
                details: [assignmentType, status],
              }
            })}
          />
          <AssignmentSection assignments={sortCareOnboardingAssignments(careOnboardingAssignments)} />
        </View>
      ),
    })
  }

  sortedSessions.forEach((sessionNumber) => {
    const sessionAssignmentsList = assignmentsBySession[sessionNumber]
    const sortedSessionAssignments = sortAssignmentsWithinSession(sessionAssignmentsList)

    renderedAssignmentsBySession.push({
      sectionKey: 'assignments',
      sessionName: `Session ${sessionNumber === 0 ? 1 : sessionNumber}`,
      content: (
        <View style={styles.sessionContainer} key={sessionNumber}>
          <ItemListPDF
            items={sortedSessionAssignments.map((assignment) => {
              const assignmentType = getAssignmentType(assignment)
              const status = AssignmentStatusesLabel[assignment.status as AssignmentStatuses] ?? assignment.status

              return {
                title: assignment.content.title,
                details: [assignmentType, status],
              }
            })}
          />
          <AssignmentSection assignments={sortedSessionAssignments} />
        </View>
      ),
    })
  })

  return renderedAssignmentsBySession
}

const ClientAssignmentsPDF = ({
  appointments,
  assignments,
  pdfContentStartDate,
  pdfContentEndDate,
  providerEpisodesInfo,
}: ClientAssignmentsPDFProps):
  | {
      episodeName: string
      assignmentsBySession: RecordsPdfAssignmentsSession[]
    }[]
  | undefined => {
  if ((pdfContentStartDate || pdfContentEndDate) && !appointments) {
    return undefined
  }
  const sessionsInDateRange = appointments?.filter((appointment) => {
    const sessionDate = getDateFromAppointment(appointment)
    return (
      (pdfContentStartDate ? sessionDate >= new Date(pdfContentStartDate) : true) &&
      (pdfContentEndDate ? sessionDate <= addDays(new Date(pdfContentEndDate), 1) : true)
    )
  })

  const filteredAssignments = assignments.filter(
    (assignment) =>
      sessionsInDateRange &&
      sessionsInDateRange.find(
        (session) =>
          session.episodeId === assignment.episode_id &&
          (session.sessionNumber === assignment.session_period ||
            (assignment.session_period === 0 && session.sessionNumber === 1)),
      ),
  )

  if (!filteredAssignments || filteredAssignments.length === 0) {
    return undefined
  }

  const assignmentsByEpisode: { text: string; id: string; assignmentsByEpisode: Assignment[] }[] | undefined =
    providerEpisodesInfo
      ? providerEpisodesInfo.map((episodeInfo) => ({
          ...episodeInfo,
          assignmentsByEpisode: filteredAssignments.filter((assignment) => assignment.episode_id === episodeInfo.id),
        }))
      : undefined

  const renderedSessionsForEpisodes: {
    episodeName: string
    assignmentsBySession: RecordsPdfAssignmentsSession[]
  }[] = []

  if (assignmentsByEpisode) {
    assignmentsByEpisode.forEach((episode) => {
      renderedSessionsForEpisodes.push({
        episodeName: episode.text,
        assignmentsBySession: AssignmentsBySession({ assignments: episode.assignmentsByEpisode }),
      })
    })
  }

  return renderedSessionsForEpisodes
}

export default ClientAssignmentsPDF
